import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { combineLatest, filter, Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import { unsubscribe } from '@fcom/core/utils';
import { ButtonMode, ButtonTheme, IconPosition, LoaderTheme } from '@fcom/ui-components';
import { RootPaths } from '@fcom/core/constants';
import { LoyaltyRoutesPath } from '@fcom/loyalty-core/interfaces/loyalty.interface';
import { LanguageService } from '@fcom/ui-translate';

import { BookingDetails, UpcomingTrip } from '../../interfaces/upcoming-trips.interface';
import { PnrWidgetState, UpcomingTripsFetchStatus } from '../../interfaces/store.interface';
import { getUpcomingTrips, getUpcomingTripsStatus } from '../../store/selectors/upcoming-trips.selectors';
import { fetchTrips } from '../../store/actions/upcoming-trips.actions';

export const MAX_SHOWN_TRIPS = 5;

@Component({
  selector: 'fin-upcoming-trips',
  templateUrl: './upcoming-trips.component.html',
  styleUrls: ['./upcoming-trips.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpcomingTripsComponent implements OnInit, OnDestroy {
  readonly LoaderTheme = LoaderTheme;
  readonly ButtonMode = ButtonMode;
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input() checkForUpgradesTripLink: boolean;

  @Output() viewDetailsClicked: EventEmitter<BookingDetails> = new EventEmitter();
  @Output() noTripsNotificationActivated: EventEmitter<boolean> = new EventEmitter();
  @Output() errorWhenFetchingTrips: EventEmitter<boolean> = new EventEmitter();

  tripsLoading = signal(true);
  upcomingTrips$: Observable<UpcomingTrip[]>;
  upcomingTripsFetchStatus$: Observable<UpcomingTripsFetchStatus>;
  seeAllMyTripsLink$: Observable<string>;

  private subscriptions = new Subscription();

  constructor(
    private store$: Store<PnrWidgetState>,
    private languageService: LanguageService
  ) {}

  viewDetailsClick(bookingDetails: BookingDetails): void {
    this.viewDetailsClicked.emit(bookingDetails);
  }

  ngOnInit(): void {
    this.seeAllMyTripsLink$ = this.languageService.lang.pipe(
      map((lang) => `/${lang}/${RootPaths.PLUS_ROOT}/${LoyaltyRoutesPath.MY_TRIPS}`)
    );
    this.upcomingTrips$ = this.store$.pipe(getUpcomingTrips());
    this.upcomingTripsFetchStatus$ = this.store$.pipe(getUpcomingTripsStatus());

    this.subscriptions.add(
      this.upcomingTripsFetchStatus$.subscribe((status: UpcomingTripsFetchStatus) => {
        if (status === UpcomingTripsFetchStatus.INITIAL) {
          this.tripsLoading.set(true);
          this.store$.dispatch(fetchTrips());
        } else {
          this.tripsLoading.set(false);
          if (status === UpcomingTripsFetchStatus.ERROR) {
            this.errorWhenFetchingTrips.emit(true);
          }
        }
      })
    );
    this.subscriptions.add(
      combineLatest([this.upcomingTrips$, this.upcomingTripsFetchStatus$])
        .pipe(filter(([trips, status]) => this.shouldShowNoTripsNotification(trips, status)))
        .subscribe(() => this.noTripsNotificationActivated.emit(true))
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  private shouldShowNoTripsNotification(trips: UpcomingTrip[], status: UpcomingTripsFetchStatus): boolean {
    return trips?.length === 0 && status === UpcomingTripsFetchStatus.FETCHED;
  }
}
